import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';

import {
  AppCenterAnalytics,
  AppCenterAnalyticsAPIMsgs,
  AppInsightsService,
} from '@shared/services';
import { MaterialModule } from '@core/modules';
import { RequestRegisterSso } from '@models/interfaces';
import { contactNumberPattern, emailPattern, userNamePattern } from '@shared/utils';
import { LogService } from '@core/services';
import { CompleteProfileService } from '../pre-complete-profile/complete-profile.service';
import { LogoComponent } from '@shared/components';
import { SetImagePathDirective } from '@shared/directives';

@Component({
  selector: 'app-complete-profile',
  templateUrl: './complete-profile.component.html',
  styleUrls: ['./complete-profile.component.scss'],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    ReactiveFormsModule,
    CommonModule,
    MaterialModule,
    LogoComponent,
    SetImagePathDirective,
  ],
})
export class CompleteProfileComponent implements OnInit, OnDestroy {
  @ViewChild('error', { read: TemplateRef }) errorTemplate!: TemplateRef<any>;
  subscription: Subscription = new Subscription();
  userForm: UntypedFormGroup;
  isError = false;
  isLoading = false;
  mobileNumber = '';
  loaded = false;
  errorMsg!: string;

  constructor(
    private readonly fb: UntypedFormBuilder,
    private readonly completeProfileService: CompleteProfileService,
    private readonly appInsightsService: AppInsightsService,
    private readonly logService: LogService
  ) {
    this.appInsightsService.trackPageViewWithoutClientId(
      `${AppCenterAnalytics.SSO_COMPLETE_PROFILE_PAGE_VIEWED}`
    );

    this.userForm = this.fb.group({
      userName: [
        this.completeProfileService?.user?.userName,
        [Validators.required, Validators.pattern(userNamePattern)],
      ],
      email: [
        this.completeProfileService.user.email,
        [Validators.required, Validators.pattern(emailPattern)],
      ],
      contactNumber: [
        this.mobileNumber,
        [
          Validators.required,
          Validators.minLength(10),
          Validators.maxLength(10),
          Validators.pattern(contactNumberPattern),
        ],
      ],
      firstName: [this.completeProfileService.user.firstName, [Validators.required]],
      lastName: [this.completeProfileService.user.lastName, [Validators.required]],
      dateOfBirth: [this.completeProfileService.user.dateOfBirth, [Validators.required]],
      cardId: [this.completeProfileService.user.cardId, [Validators.required]],
      clientId: [this.completeProfileService.user.clientId, [Validators.required]],
      personCode: [this.completeProfileService.user.personCode, [Validators.required]],
      objectId: [''],
    });
  }

  ngOnInit(): void {
    this.getContactInfo();
  }

  private getContactInfo(): void {
    this.loaded = true;
    if (this.completeProfileService.user.contactNumber) {
      this.mobileNumber = this.completeProfileService.user.contactNumber;
      this.userForm.patchValue({
        contactNumber: this.mobileNumber,
      });
      this.appInsightsService.trackPageViewWithoutClientId(
        `${AppCenterAnalytics.SSO_CONTACT_DETAILS_FETCHED}`,
        { ...this.getProperties(), data: { contactNumber: this.mobileNumber } }
      );
    }
  }

  public complete(): void {
    this.appInsightsService.trackPageViewWithoutClientId(
      `${AppCenterAnalytics.SSO_COMPLETE_PROFILE_REGISTER_BUTTON_CLICKED}`,
      this.getProperties()
    );
    this.registerSsoMember();
  }

  private registerSsoMember(): void {
    this.isError = false;
    this.isLoading = true;
    const request: RequestRegisterSso = {
      username: this.userForm.controls.userName.value,
      clientId: this.userForm.controls.clientId.value,
      cardId: this.userForm.controls.cardId.value,
      firstName: this.userForm.controls.firstName.value,
      lastName: this.userForm.controls.lastName.value,
      birthdate: this.userForm.controls.dateOfBirth.value,
      email: this.userForm.controls.email.value,
      contactNumber: this.userForm.controls.contactNumber.value,
      agreeTermsAndConditions: true,
    };
    this.subscription.add(
      this.completeProfileService.registerSsoMember(request).subscribe(
        (resp) => {
          this.isLoading = false;
          this.logService.log(`[CompleteProfileComponent] Success`, resp);
          this.logService.log(
            `[CompleteProfileComponent] Redirecting to: location ${resp.headers.get('Location')}`
          );

          const responseText = AppCenterAnalyticsAPIMsgs.SSO_MEMBER_REGISTERED;
          this.appInsightsService.trackPageViewWithoutClientId(
            AppCenterAnalytics.SSO_COMPLETE_PROFILE_REGISTER_API_CALL,
            { ...this.getProperties(), data: request, Response: responseText }
          );

          window.location.replace(resp.headers.get('Location'));
        },
        (error) => {
          this.logService.error(`[CompleteProfileComponent] Error`, error);
          this.appInsightsService.trackPageViewWithoutClientId(
            AppCenterAnalytics.SSO_COMPLETE_PROFILE_REGISTER_API_ERROR,
            { ...this.getProperties(), data: request, Response: error }
          );
          this.isError = true;
          this.errorMsg = error;
          this.isLoading = false;
        }
      )
    );
  }

  public getProperties(): Record<string, any> {
    const properties: Record<string, any> = {
      userID: this.userForm.controls.userName.value,
      clientID: this.userForm.controls.clientId.value,
      Name: `${this.userForm.controls.firstName.value} ${this.userForm.controls.lastName.value}`,
      CardID: this.userForm.controls.cardId.value,
      Email: this.userForm.controls.email.value,
    };
    return properties;
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
